import React, { useContext, useEffect } from "react";

import { Preview } from "@creatomate/preview";
import { Dropdown } from "@culturehq/components";

import { CreatomateTemplate, Subtitle } from "chq";

import { useProfile } from "app/context";
import CheckAspectRatio from "app/utils/CheckAspectRatio";

import AspectRatioOption from "./AspectRatioOption";
import { CreatomateContext } from "../../contexts/VideoStudioContext";
import { isSubtitleText, loadSubtitle, removeSubtitlesElements } from "../../sidebar/tabs/subtitle/utils";

interface AspectRatioDropdownProps {
  open: boolean;
  preview: Preview;
}

const AspectRatioDropdown: React.FC<AspectRatioDropdownProps> = ({
  open, preview
}) => {
  const { profile: { organization } } = useProfile();

  const { ready, state } = preview || { ready: false };
  const { elements } = state || { elements: [] };

  const { data, handleUpdateData, handleAutosaveDraft } = useContext(CreatomateContext);
  const {
    aspectRatio,
    aspectRatioStringValue,
    burnSubtitles,
    creatomateJson,
    dimensions,
    story,
    subtitles
  } = data;

  const getVideoWidth = () => creatomateJson?.width || story?.media?.width || dimensions?.width;
  const getVideoHeight = () => creatomateJson?.height || story?.media?.height || dimensions?.height;

  useEffect(
    () => {
      let isMounted = true;

      if (open && ready && aspectRatio && isMounted) {
        let newAspectRatio = "original";
        if (aspectRatio > 0 && aspectRatio <= 0.5625) {
          newAspectRatio = "tall-portrait";
        } else if (aspectRatio > 0.5625 && aspectRatio <= 0.8) {
          newAspectRatio = "portrait";
        } else if (aspectRatio > 0.8 && aspectRatio <= 1) {
          newAspectRatio = "square";
        } else if (aspectRatio > 1 && aspectRatio <= 1.35) {
          newAspectRatio = "landscape";
        } else if (aspectRatio > 1.35) {
          newAspectRatio = "wide-landscape";
        }
        if (isMounted) handleAspectRatio(newAspectRatio);
      }

      return () => {
        isMounted = false;
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, ready, aspectRatio]
  );

  // when the aspect ratio changes, we need to see if we need to recalculate
  // the subtitles text with white spaces
  const checkSubtitlesReload = async (currentSubtitle: Subtitle, value: string) => {
    const reloadSubtitles = (value.includes("portrait") && !aspectRatioStringValue.includes("portrait"))
      || (!value.includes("portrait") && aspectRatioStringValue.includes("portrait"));
    // due to the change in aspect ratio, we need to reload the subtitles
    if (reloadSubtitles) {
      const subtitleElement = elements.find(element => isSubtitleText(element.source.name));
      await removeSubtitlesElements(handleAutosaveDraft, preview, subtitles);

      const elementId = await loadSubtitle(
        handleAutosaveDraft,
        subtitles,
        currentSubtitle,
        preview,
        elements,
        0,
        organization.name,
        burnSubtitles,
        subtitleElement,
        currentSubtitle.language
      );
      if (elementId) await preview.setActiveElements([elementId]);
    }
  };

  const handleAspectRatio = async (value: string) => {
    const { width, height } = CheckAspectRatio.calculate({
      width: getVideoWidth(),
      height: getVideoHeight()
    }, value);

    await preview.applyModifications({ width, height });
    handleUpdateData({ aspectRatioStringValue: value });

    const currentSubtitle = subtitles.find(subtitle => subtitle.active);
    if (currentSubtitle) await checkSubtitlesReload(currentSubtitle, value);
  };

  return (
    <div>
      <Dropdown selected={aspectRatioStringValue} onChange={handleAspectRatio}>
        <Dropdown.Button className="btn-dropdown">
          <AspectRatioOption aspectRatio={aspectRatioStringValue} />
        </Dropdown.Button>
        <Dropdown.ListBox className="AspectRatio-Options">
          <Dropdown.Option className="AspectRatio-Option" value="tall-portrait">
            <AspectRatioOption aspectRatio="tall-portrait" />
          </Dropdown.Option>
          <Dropdown.Option className="AspectRatio-Option" value="portrait">
            <AspectRatioOption aspectRatio="portrait" />
          </Dropdown.Option>
          <Dropdown.Option className="AspectRatio-Option" value="square">
            <AspectRatioOption aspectRatio="square" />
          </Dropdown.Option>
          <Dropdown.Option className="AspectRatio-Option" value="landscape">
            <AspectRatioOption aspectRatio="landscape" />
          </Dropdown.Option>
          <Dropdown.Option className="AspectRatio-Option" value="wide-landscape">
            <AspectRatioOption aspectRatio="wide-landscape" />
          </Dropdown.Option>
        </Dropdown.ListBox>
      </Dropdown>
    </div>
  );
};

export default AspectRatioDropdown;
